import type { EditorialOutput, EditorialPrenota, EditorialXReview } from 'src/types/editorial';

/**
 * Why a prenota did or did not become a nota.
 *
 * The pipeline applies three conditions in `run_editorial_transformations`, in this order:
 *
 *   1. the prenota's status is `ready_for_editorial` or `needs_review` (a blocked one never drafts);
 *   2. its story cleared X Social Review with a score >= EDITORIAL_MIN_X_SCORE — a story with *no*
 *      X review is gated out too, not drafted with an unknown score;
 *   3. the draft itself came back publishable.
 *
 * This module reproduces that ladder for the reader. It does not re-derive the pipeline's decision;
 * it explains it, and where the browser cannot know something it says so instead of guessing.
 */

const DRAFTABLE_STATUS = new Set(['ready_for_editorial', 'needs_review']);
const PUBLISHABLE = 'pending_human_review';

export type GateState = 'pass' | 'fail' | 'pending' | 'unknown';
export type GateStep = { label: string; state: GateState; detail: string };

/** story_id → overall_score. A cluster's score covers every story it reviewed. */
export function buildScoreIndex(reviews: EditorialXReview[]): Map<string, number> {
  const scores = new Map<string, number>();
  for (const review of reviews) {
    const score = review.payload.overall_score;
    if (score === undefined) continue;
    for (const storyId of [...(review.payload.source_story_ids ?? []), review.payload.story_id]) {
      if (storyId) scores.set(storyId, score);
    }
  }
  return scores;
}

/**
 * The lowest score that actually produced a nota in this stream — an upper bound on the server's
 * EDITORIAL_MIN_X_SCORE, which the browser is never told.
 *
 * Below it, "the social floor stopped this" is a safe reading. At or above it, a story without a
 * nota is still being drafted, and calling that "rejected" would be a lie the reader acts on.
 */
export function observedSocialFloor(outputs: EditorialOutput[], scores: Map<string, number>): number | null {
  const drafted = outputs.map(output => scores.get(output.storyId)).filter((score): score is number => score !== undefined);
  return drafted.length > 0 ? Math.min(...drafted) : null;
}

export function storyGate(
  prenota: EditorialPrenota,
  scores: Map<string, number>,
  outputs: EditorialOutput[],
  observedFloor: number | null
): GateStep[] {
  const status = prenota.payload.status;
  const eligible = DRAFTABLE_STATUS.has(status);
  const score = scores.get(prenota.storyId);
  const own = outputs.filter(output => output.storyId === prenota.storyId);
  const publishable = own.filter(output => output.payload.status === PUBLISHABLE);

  const statusStep: GateStep = eligible
    ? { label: 'Estado de la prenota', state: 'pass', detail: 'Puede pasar a redacción.' }
    : {
        label: 'Estado de la prenota',
        state: 'fail',
        detail: 'Una prenota bloqueada o inválida nunca se redacta, sin importar su relevancia.',
      };

  const socialStep: GateStep =
    score === undefined
      ? {
          label: 'Relevancia social (X)',
          state: 'fail',
          detail: 'Sin revisión de X no hay score, y una historia sin score queda fuera por diseño.',
        }
      : {
          label: 'Relevancia social (X)',
          state: observedFloor !== null && score < observedFloor ? 'fail' : 'pass',
          detail:
            observedFloor !== null && score < observedFloor
              ? `${score} puntos, por debajo del ${observedFloor} más bajo que sí produjo nota en esta transmisión.`
              : `${score} puntos.`,
        };

  const noteStep = buildNoteStep(own, publishable, eligible, score, observedFloor);

  return [statusStep, socialStep, noteStep];
}

function buildNoteStep(
  own: EditorialOutput[],
  publishable: EditorialOutput[],
  eligible: boolean,
  score: number | undefined,
  observedFloor: number | null
): GateStep {
  if (publishable.length > 0) {
    const profiles = publishable.map(output => output.profileId).join(', ');
    return { label: 'Nota', state: 'pass', detail: `Generada · ${profiles}` };
  }
  if (own.length > 0) {
    const statuses = [...new Set(own.map(output => output.payload.status))].join(', ');
    return { label: 'Nota', state: 'fail', detail: `Se redactó y no pasó: ${statuses}` };
  }
  if (!eligible || score === undefined) {
    return { label: 'Nota', state: 'fail', detail: 'No se intentó: la historia no llegó al paso de redacción.' };
  }
  if (observedFloor !== null && score < observedFloor) {
    return { label: 'Nota', state: 'fail', detail: 'No se intentó: quedó por debajo del piso de relevancia social.' };
  }
  return {
    label: 'Nota',
    state: 'pending',
    detail: 'Todavía no. La historia pasó los filtros; su redacción no ha terminado o no se ha corrido.',
  };
}
